import { Injectable } from '@angular/core';
import { Observable, of, firstValueFrom } from 'rxjs';
import { Parcel, Waybill, ShipmentRequestService } from './shipment-request.service';
import { Warehouse } from '../interfaces/Warehouses.interfaces';
import jsPDF from 'jspdf';
import JsBarcode from 'jsbarcode';
import * as QRCode from 'qrcode';

/**
 * Service for generating parcel labels with barcodes
 * Generates PDF files with one label per page for waybill parcels
 * Uses JsBarcode library for professional Code-128 barcode generation
 * Also supports PNG generation as backup option
 */
@Injectable({
    providedIn: 'root'
})
export class LabelGenerationService {  // Standard label dimensions in mm (adjustable)
    private readonly LABEL_WIDTH = 100; // mm (10cm)
    private readonly LABEL_HEIGHT = 140; // mm (14cm)

    // Codice cliente per cui l'anagrafica mittente è quella del magazzino di origine
    private readonly AUTOVANTI_CLIENT_CODE = 'CLT00011';
    // Cache dei magazzini recuperati dal backend per evitare chiamate ripetute durante la stessa generazione
    private warehouseCache = new Map<string, Warehouse | null>();

    constructor(private shipmentRequestService: ShipmentRequestService) { }

    /**
     * Recupera (con cache) l'anagrafica del magazzino di origine della bolla.
     * Ritorna null in caso di errore o codice mancante.
     */
    private async getOriginWarehouse(warehouseCode?: string): Promise<Warehouse | null> {
        if (!warehouseCode) {
            return null;
        }
        if (this.warehouseCache.has(warehouseCode)) {
            return this.warehouseCache.get(warehouseCode) ?? null;
        }
        try {
            const warehouse = await firstValueFrom(this.shipmentRequestService.getWarehouseDetails(warehouseCode));
            //console.log('Magazzino di origine recuperato:', warehouseCode, warehouse);
            this.warehouseCache.set(warehouseCode, warehouse ?? null);
            return warehouse ?? null;
        } catch (error) {
            console.warn('Impossibile recuperare i dati del magazzino di origine:', warehouseCode, error);
            this.warehouseCache.set(warehouseCode, null);
            return null;
        }
    }

    /**
     * Costruisce la stringa indirizzo a partire dai campi anagrafici del magazzino.
     */
    private buildWarehouseAddress(warehouse: Warehouse): string {
        const streetPart = [warehouse.warehouseAddressStreet, warehouse.warehouseAddressNumber]
            .filter(part => !!part)
            .join(', ');
        const cityPart = [warehouse.warehouseAddressZip, warehouse.warehouseAddressCity]
            .filter(part => !!part)
            .join(' ');
        const provincePart = warehouse.warehouseAddressProvince ? `(${warehouse.warehouseAddressProvince})` : '';
        return [streetPart, [cityPart, provincePart].filter(part => !!part).join(' ')]
            .filter(part => !!part)
            .join(', ');
    }
  /**
   * Generate labels for all parcels of a waybill
   * @param parcels Array of parcels for the waybill
   * @param waybillCode The waybill code
   * @param dealerInfo Dealer information for the label
   * @returns Observable with success status
   */  generateWaybillLabels(parcels: Parcel[], waybill: Waybill, dealerInfo?: any): Observable<boolean> {
        const generateAsync = async () => {
            try {
                // Create PDF document with custom page size matching label dimensions
                const pdf = new jsPDF({
                    orientation: this.LABEL_WIDTH > this.LABEL_HEIGHT ? 'landscape' : 'portrait',
                    unit: 'mm',
                    format: [this.LABEL_WIDTH, this.LABEL_HEIGHT]
                });

                // Process each parcel on a separate page
                for (let index = 0; index < parcels.length; index++) {
                    const parcel = parcels[index];
                    
                    if (index > 0) {
                        pdf.addPage([this.LABEL_WIDTH, this.LABEL_HEIGHT]); // Add new page with same dimensions
                    }

                    await this.drawParcelLabelOnPDF(pdf, parcel, waybill, dealerInfo, this.LABEL_WIDTH, this.LABEL_HEIGHT, index + 1, parcels.length);
                }

                // Download the PDF
                const filename = `Labels_${waybill.waybillCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
                pdf.save(filename);

                return true;
            } catch (error) {
                console.error('Error generating waybill labels:', error);
                return false;
            }
        };

        return new Observable(observer => {
            generateAsync().then(result => {
                observer.next(result);
                observer.complete();
            }).catch(error => {
                console.error('Error in async label generation:', error);
                observer.next(false);
                observer.complete();
            });
        });
    }
  /**
   * Generate a single label for a parcel
   * @param parcel The parcel data
   * @param waybillCode The waybill code
   * @param dealerInfo Dealer information for the label
   * @returns Observable with success status
   */  generateSingleParcelLabel(parcel: Parcel, waybill: Waybill, dealerInfo?: any): Observable<boolean> {
        const generateAsync = async () => {
            // Create PDF document with custom page size matching label dimensions
            const pdf = new jsPDF({
                orientation: this.LABEL_WIDTH > this.LABEL_HEIGHT ? 'landscape' : 'portrait',
                unit: 'mm',
                format: [this.LABEL_WIDTH, this.LABEL_HEIGHT]
            });

            await this.drawParcelLabelOnPDF(pdf, parcel, waybill, dealerInfo, this.LABEL_WIDTH, this.LABEL_HEIGHT, 1, 1);

            // Download the PDF
            const filename = `Label_${parcel.parcelCode}_${new Date().toISOString().slice(0, 10)}.pdf`;
            pdf.save(filename);

            return true;
        };

        return new Observable<boolean>(observer => {
            generateAsync().then(result => {
                observer.next(result);
                observer.complete();
            }).catch(error => {
                console.error('Error generating single parcel label:', error);
                observer.next(false);
                observer.complete();
            });
        });
    }  /**
   * Draw a single parcel label directly on a PDF page
   * @param pdf jsPDF instance
   * @param parcel Parcel data
   * @param waybillCode Waybill code
   * @param dealerInfo Dealer information
   * @param labelWidth Label width in mm
   * @param labelHeight Label height in mm
   */    private async drawParcelLabelOnPDF(
        pdf: jsPDF,
        parcel: Parcel,
        waybill: Waybill,
        dealerInfo?: any,
        labelWidth: number = this.LABEL_WIDTH,
        labelHeight: number = this.LABEL_HEIGHT,
        parcelNumber: number = 1,
        totalParcels: number = 1
    ): Promise<void> {// Use the full page size (no centering needed since page = label size)
        const startX = 3; // Small margin
        const startY = 3; // Small margin
        const usableWidth = labelWidth - 6; // Account for margins
        const usableHeight = labelHeight - 6; // Account for margins        // Add company logo image at the top-left corner
        // const logoX = startX;
        // const logoY = startY;
        // const logoWidth = 28; // mm
        // const logoHeight = 10; // mm        // Use the real logo PNG image - path verified to exist
        // const logoUrl = '/img/logo-bertani-nero.png';

        // // For now, use the professional placeholder since async image loading 
        // // in PDF generation can cause timing issues. The actual image can be 
        // // implemented with proper async/await pattern in the future.
        // this.addLogoToPDF(pdf, logoX, logoY, logoWidth, logoHeight);
        
        // TODO: Implement proper async image loading when refactoring to async PDF generation
        // this.loadImageLogoSync(pdf, logoUrl, logoX, logoY, logoWidth, logoHeight);
        // Draw border
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.rect(1, 1, labelWidth - 2, labelHeight - 2);

        // Company header - left aligned at top
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(16);
        pdf.setTextColor(0, 0, 0);
        const isAutoVanti = parcel.parcelClientCode === this.AUTOVANTI_CLIENT_CODE;

        // Per AutoVanti l'anagrafica mittente proviene dal magazzino di origine della bolla
        let companyName = isAutoVanti ? 'AUTOVANTI' : 'BERTANI TRASPORTI S.P.A.';
        let companyAddress = isAutoVanti
            ? 'Viale Sicilia, 130, 20900 Monza (MB)'
            : 'Via Cavour, 58, 46043 Castiglione delle Stiviere (Mantova)';

        if (isAutoVanti) {
            const originWarehouse = await this.getOriginWarehouse(waybill?.waybillOriginLocation);
            if (originWarehouse) {
                companyName = originWarehouse.warehouseName || companyName;
                const address = this.buildWarehouseAddress(originWarehouse);
                if (address) {
                    companyAddress = address;
                }
            }
        }

        pdf.text(companyName, startX, startY + 8, { align: 'left' });

        // Small left aligned address under the title
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(6);
        pdf.setTextColor(80, 80, 80);
        pdf.text(companyAddress, startX, startY + 12, { align: 'left' });
        pdf.setTextColor(0, 0, 0); // Reset to black        // Add QR code for parcel code in the title area (top-right corner)
        if (parcel.parcelBarcode) {
            try {
                const qrCodeSize = 15; // mm
                const qrCodeX = startX + usableWidth - qrCodeSize;
                const qrCodeY = startY - 1; // Alzato di 1mm
                
                // Generate QR code
                const qrCodeDataUrl = await this.generateQRCode(parcel.parcelCode, 100);
                
                // Add QR code to PDF
                pdf.addImage(qrCodeDataUrl, 'PNG', qrCodeX, qrCodeY, qrCodeSize, qrCodeSize);
                
                // Add small label under QR code
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(5);
                pdf.setTextColor(100, 100, 100);
                //pdf.text('QR Collo', qrCodeX + qrCodeSize / 2, qrCodeY + qrCodeSize + 3, { align: 'center' });
                pdf.setTextColor(0, 0, 0); // Reset to black
            } catch (error) {
                console.warn('Could not generate QR code for parcel:', parcel.parcelBarcode, error);
            }
        }

        // Divider line after destination
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 14, startX + usableWidth, startY + 14);

        // Destination label (small text under company name)
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.text('Destinazione:', startX, startY + 18, { align: 'left' });


        if (dealerInfo.dealerCode) {
            pdf.text(`[ Cod: ${dealerInfo.dealerCode} ]`, startX + 20, startY + 18, { align: 'left' });
        }

        // Estimated delivery date (waybillEstimate)
        if (parcel.parcelEstimatedDeliveryDate) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            const deliveryDate = new Date(parcel.parcelEstimatedDeliveryDate);
            const formattedDate = `${deliveryDate.getDate().toString().padStart(2, '0')}/` +
            `${(deliveryDate.getMonth() + 1).toString().padStart(2, '0')}/` +
            `${deliveryDate.getFullYear()} ` +
            `${deliveryDate.getHours().toString().padStart(2, '0')}:` +
            `${deliveryDate.getMinutes().toString().padStart(2, '0')}`;
            const deliveryText = `Consegna Entro: ${formattedDate}`;
            // Calculate right position
            const boxWidth = 52;
            const boxHeight = 6;
            const boxX = startX + usableWidth - boxWidth;
            const boxY = startY + 14;
            // Draw background rectangle (gray)
            pdf.setFillColor(0, 0, 0);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.2);
            pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD'); // Fill and draw border
            // Draw text in black
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.text(deliveryText, boxX + 2, boxY + 4, { align: 'left' });
            pdf.setTextColor(0, 0, 0); // Reset to black for next texts
        }

        // Destination name (dealer name in bold, larger font)
        if (dealerInfo && dealerInfo.dealerName) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(10);
            const destinationName = dealerInfo.dealerName.length > 60
                ? dealerInfo.dealerName.substring(0, 60) + '...'
                : dealerInfo.dealerName;
            pdf.text(destinationName, startX, startY + 26, { align: 'left' });
        }

        // Dealer comprehensive operational information (full width, organized layout)
        if (dealerInfo) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(9);
            let dealerY = startY + 32;

            // Left column for address info
            const leftColX = startX;
            const rightColX = startX + usableWidth / 2;

            // Operating Address (left column)
            if (dealerInfo.dealerOperatingAddressStreet) {
                const street = dealerInfo.dealerOperatingAddressStreet.length > 60
                    ? dealerInfo.dealerOperatingAddressStreet.substring(0, 60) + '...'
                    : dealerInfo.dealerOperatingAddressStreet;
                pdf.text(street, leftColX, dealerY);
            }

            // Dealer Code (right column)
            //   if (dealerInfo.dealerCode) {
            //     pdf.text(`Cod: ${dealerInfo.dealerCode}`, rightColX, dealerY);
            //   }
            dealerY += 6;

            // City and Province (left column)
            let cityProvince = '';
            if (dealerInfo.dealerOperatingAddressCity) {
                cityProvince = dealerInfo.dealerOperatingAddressCity;
            }
            if (dealerInfo.dealerOperatingAddressProvince) {
                cityProvince += ` (${dealerInfo.dealerOperatingAddressProvince})`;
            }
            if (cityProvince) {
                const cityProvinceText = cityProvince.length > 25
                    ? cityProvince.substring(0, 25) + '...'
                    : cityProvince;
                pdf.text(cityProvinceText, leftColX, dealerY);
            }

            dealerY += 6;
            // Phone (right column)
            const phone = dealerInfo.dealerPhone ? dealerInfo.dealerPhone : 'N/A';
            pdf.text(`Tel: ${phone}`, rightColX, dealerY);
            

            // CAP (left column)
            const zip = dealerInfo.dealerOperatingAddressZip ? dealerInfo.dealerOperatingAddressZip : 'N/A';
            pdf.text(`CAP: ${zip}`, leftColX, dealerY);
            dealerY += 6;

            // VAT Number and Fiscal Code (left column, same row)
            let vatNumber = dealerInfo.dealerVatNumber ? dealerInfo.dealerVatNumber : 'N/A';
            let fiscalCode = dealerInfo.dealerFiscalCode ? dealerInfo.dealerFiscalCode : 'N/A';
            pdf.text(`P.IVA: ${vatNumber}`, leftColX, dealerY);
            pdf.text(`C.F: ${fiscalCode}`, rightColX, dealerY);
            dealerY += 6;
        }

        // Second divider line after dealer info
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 56, startX + usableWidth, startY + 56);    // Title line with waybill and parcel codes
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Parametri Spedizione:', startX, startY + 60);

        // Waybill and parcel codes on same line
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Bolla:', startX, startY + 68);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(`${waybill.waybillCode}`, startX + 13, startY + 68);

        // Tour parameter at the bottom of the section (full width, black background)
        if (waybill.waybillDeliveryType) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(15); // Aumentato da 10 a 15
            const deliveryText = `Tour: ${waybill.waybillDeliveryType}`;
            // Full width box
            const boxWidth = usableWidth;
            const boxHeight = 6;
            const boxX = startX;
            const boxY = startY + 74; // Spostato in fondo alla sezione
            // Draw background rectangle (black)
            pdf.setFillColor(0, 0, 0);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.2);
            pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD'); // Fill and draw border
            // Draw text in white (centered vertically and horizontally)
            pdf.setTextColor(255, 255, 255);
            pdf.text(deliveryText, boxX + boxWidth / 2, boxY + boxHeight / 2 + 2, { align: 'center' });
            pdf.setTextColor(0, 0, 0); // Reset to black for next texts
        }        pdf.setFont('helvetica', 'bold');
        // pdf.setFontSize(8);
        // pdf.text('Collo:', startX, startY + 74);
        // pdf.setFont('helvetica', 'normal');
        // pdf.setFontSize(11);
        // pdf.text(`${parcel.parcelCode || 'N/A'}`, startX + 13, startY + 74);

        // pdf.setFont('helvetica', 'bold');
        // pdf.setFontSize(8);
        // pdf.text('Metodo:', startX, startY + 80);
        // pdf.setFont('helvetica', 'normal');
        // pdf.setFontSize(11);
        // pdf.text(`${waybill.waybillDeliveryMethod || 'N/A'}`, startX + 13, startY + 80);

        // Divider line after shipment parameters section
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.line(startX, startY + 82, startX + usableWidth, startY + 82);    // Title line for parcel parameters
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(0, 0, 0);
        pdf.text('Parametri Colli:', startX, startY + 86);

        // Parcel type box on the right
        if (parcel.parcelType) {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            const deliveryText = `${parcel.parcelType}`;
            // Calculate right position
            const boxWidth = 15;
            const boxHeight = 15;
            const boxX = startX + usableWidth - boxWidth;
            const boxY = startY + 82;
            // Draw background rectangle (white)
            pdf.setFillColor(255, 255, 255);
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.3);
            pdf.rect(boxX, boxY, boxWidth, boxHeight, 'FD'); // Fill and draw border
            // Draw text in black
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(25);
            pdf.text(deliveryText, boxX + boxWidth / 2, boxY + boxHeight / 2 + 3, { align: 'center' });
            pdf.setTextColor(0, 0, 0); // Reset to black for next texts
        }

        // Parcel counter box below parcel type box
        const counterBoxWidth = 15;
        const counterBoxHeight = 6;
        const counterBoxX = startX + usableWidth - counterBoxWidth;
        const counterBoxY = startY + 82 + 15 + 1; // Below parcel type box with 1mm gap
        // Draw background rectangle (white)
        pdf.setFillColor(255, 255, 255);
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.3);
        pdf.rect(counterBoxX, counterBoxY, counterBoxWidth, counterBoxHeight, 'FD'); // Fill and draw border
        // Draw text in black
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text(`${parcelNumber}/${totalParcels}`, counterBoxX + counterBoxWidth / 2, counterBoxY + counterBoxHeight / 2 + 1, { align: 'center' });
        pdf.setTextColor(0, 0, 0); // Reset to black for next texts

        // Parcel parameters with bullet points
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        let paramY = startY + 92;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Peso:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
            parcel.parcelWeight != null && parcel.parcelWeight !== 0
                ? `${parcel.parcelWeight} kg`
                : '-',
            startX + 18,
            paramY
        );
        paramY += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Volume:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
            parcel.parcelVolume != null && parcel.parcelVolume !== 0
                ? `${parcel.parcelVolume} m³`
                : '-',
            startX + 18,
            paramY
        );
        paramY += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Dimensioni:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.text(
            `${parcel.parcelLength ?? 0} x ${parcel.parcelWidth ?? 0} x ${parcel.parcelHeight ?? 0} cm`,
            startX + 18,
            paramY
        );
        paramY += 6;

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        pdf.text('Descrizione:', startX, paramY);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        if (parcel.parcelDescription && parcel.parcelDescription.trim().length > 0) {
            const description = parcel.parcelDescription.length > 40
                ? parcel.parcelDescription.substring(0, 40)
                : parcel.parcelDescription;
            const words = description.split(/\s+/).filter(w => w.length > 0);
            const descriptionLines: string[] = [];
            let currentLine = '';
            for (const word of words) {
                const candidate = currentLine.length === 0 ? word : currentLine + ' ' + word;
                if (candidate.length <= 40) {
                    currentLine = candidate;
                } else {
                    if (currentLine.length > 0) {
                        descriptionLines.push(currentLine);
                    }
                    currentLine = word;
                }
            }
            if (currentLine.length > 0) {
                descriptionLines.push(currentLine);
            }
            descriptionLines.forEach((line, idx) => {
                pdf.text(line, startX + 18, paramY + idx * 5);
            });
            paramY += 6 + (descriptionLines.length - 1) * 5;
        } else {
            pdf.text('-', startX + 18, paramY);
            paramY += 6;
        }


        // Barcode at bottom of label (moved higher up)
        pdf.setTextColor(0, 0, 0);
        if (parcel.parcelBarcode) {
            // Calculate barcode dimensions and center it
            const barcodeWidth = usableWidth * 0.8;
            const barcodeX = startX + (usableWidth - barcodeWidth) / 2; // Center horizontally

            // Label for barcode (centered)
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(5);
            //pdf.text(`Barcode: ${parcel.parcelBarcode}`, startX + usableWidth / 2, startY + usableHeight - 12, { align: 'center' });
            // Draw barcode higher up for better visibility
            this.drawSimpleBarcodeOnPDF(pdf, parcel.parcelBarcode, barcodeX, startY + usableHeight - 18, barcodeWidth, 12);
        }

        // Reset text color
        pdf.setTextColor(0, 0, 0);
    }  /**
   * Draw a Code-128 barcode using JsBarcode library
   * @param pdf jsPDF instance
   * @param code Barcode string
   * @param x X position in mm
   * @param y Y position in mm
   * @param width Barcode width in mm
   * @param height Barcode height in mm
   */
    private drawSimpleBarcodeOnPDF(
        pdf: jsPDF,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        try {
            // Create a temporary canvas for barcode generation
            const canvas = document.createElement('canvas');

            // Generate Code-128 barcode using JsBarcode
            JsBarcode(canvas, code, {
                format: "CODE128",
                width: 2,
                height: 100,
                displayValue: false, // We'll add text separately
                margin: 0,
                background: "#ffffff",
                lineColor: "#000000"
            });

            // Convert canvas to data URL
            const barcodeDataUrl = canvas.toDataURL('image/png');

            // Add barcode image to PDF
            pdf.addImage(barcodeDataUrl, 'PNG', x, y, width, height);
            // Add the code text below the barcode
            pdf.setFont('courier', 'bold');
            pdf.setFontSize(8);
            pdf.text(code, x + width / 2, y + height + 4, { align: 'center' }); // Increased from +2 to +6

        } catch (error) {
            console.error('Error generating barcode with JsBarcode:', error);
            // Fallback to manual pattern generation
            this.drawFallbackBarcode(pdf, code, x, y, width, height);
        }
    }

    /**
     * Fallback barcode drawing method
     * @param pdf jsPDF instance
     * @param code Barcode string
     * @param x X position in mm
     * @param y Y position in mm
     * @param width Barcode width in mm
     * @param height Barcode height in mm
     */
    private drawFallbackBarcode(
        pdf: jsPDF,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        // Simple fallback - draw a rectangle with text
        pdf.setDrawColor(0, 0, 0);
        pdf.setLineWidth(0.2);
        pdf.rect(x, y, width, height);
        pdf.setFont('courier', 'bold');
        pdf.setFontSize(6);
        pdf.text('BARCODE', x + width / 2, y + height / 2, { align: 'center' });
        pdf.text(code, x + width / 2, y + height + 6, { align: 'center' }); // Increased from +2 to +6
    }


    /**
     * Draw a single parcel label on the canvas
     * @param ctx Canvas 2D context
     * @param parcel Parcel data
     * @param x X position on canvas
     * @param y Y position on canvas
     * @param width Label width
     * @param height Label height
     * @param waybillCode Waybill code
     * @param dealerInfo Dealer information
     */
    private drawParcelLabel(
        ctx: CanvasRenderingContext2D,
        parcel: Parcel,
        x: number,
        y: number,
        width: number,
        height: number,
        waybillCode: string,
        dealerInfo?: any
    ): void {
        // Draw border
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.strokeRect(x, y, width, height);

        // Add logo placeholder in top-left corner
        const logoWidth = 60; // px
        const logoHeight = 30; // px
        ctx.strokeStyle = '#0066cc';
        ctx.lineWidth = 1;
        ctx.strokeRect(x + 10, y + 10, logoWidth, logoHeight);
        ctx.fillStyle = '#0066cc';
        ctx.font = 'bold 12px Arial';
        ctx.textAlign = 'center';
        ctx.fillText('BERTANI', x + 10 + logoWidth / 2, y + 10 + logoHeight / 2 + 4);

        // Set text styles
        ctx.fillStyle = '#000000';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'left';


        // Divider line
        ctx.strokeStyle = '#cccccc';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x + 10, y + 45);
        ctx.lineTo(x + width - 10, y + 45);
        ctx.stroke();

        // Waybill info
        ctx.font = 'bold 14px Arial';
        ctx.fillStyle = '#000000';
        ctx.fillText(`Bolla: ${waybillCode}`, x + 10, y + 65);

        // Parcel info
        ctx.font = '12px Arial';
        ctx.fillText(`Collo: ${parcel.parcelCode || 'N/A'}`, x + 10, y + 85);

        // Parcel details
        ctx.font = '10px Arial';
        let detailY = y + 105;

        if (parcel.parcelWeight) {
            ctx.fillText(`Peso: ${parcel.parcelWeight} kg`, x + 10, detailY);
            detailY += 15;
        }

        if (parcel.parcelVolume) {
            ctx.fillText(`Volume: ${parcel.parcelVolume} m³`, x + 10, detailY);
            detailY += 15;
        }

        if (parcel.parcelDescription) {
            ctx.fillText(`Desc: ${parcel.parcelDescription.substring(0, 35)}`, x + 10, detailY);
        }

        // Dealer info on the right side
        if (dealerInfo) {
            ctx.font = '10px Arial';
            ctx.textAlign = 'right';
            let rightY = y + 65;

            if (dealerInfo.dealerName) {
                ctx.fillText(dealerInfo.dealerName.substring(0, 25), x + width - 10, rightY);
                rightY += 12;
            }

            if (dealerInfo.dealerOperatingAddressCity) {
                ctx.fillText(`${dealerInfo.dealerOperatingAddressCity}`, x + width - 10, rightY);
                rightY += 12;
            }

            if (dealerInfo.dealerOperatingAddressProvince) {
                ctx.fillText(`(${dealerInfo.dealerOperatingAddressProvince})`, x + width - 10, rightY);
            }
        }

        // Special flags (middle section)
        ctx.textAlign = 'left';
        ctx.font = 'bold 10px Arial';
        ctx.fillStyle = '#ff0000';

        let flagY = y + height - 60;
        if (parcel.parcelFragile === 'Y') {
            ctx.fillText('FRAGILE', x + 10, flagY);
            flagY += 12;
        }

        if (parcel.parcelHazardousMaterial === 'Y') {
            ctx.fillText('PERICOLOSO', x + 10, flagY);
            flagY += 12;
        }

        if (parcel.parcelTemperatureSensitive === 'Y') {
            ctx.fillText('TEMP. SENSIBILE', x + 10, flagY);
        }    // Barcode at bottom (centered)
        ctx.fillStyle = '#000000';
        if (parcel.parcelBarcode) {
            // Calculate barcode dimensions and center it
            const barcodeWidth = 180;
            const barcodeX = x + (width - barcodeWidth) / 2; // Center horizontally

            // Label for barcode (centered)
            ctx.font = '10px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(`Barcode: ${parcel.parcelBarcode}`, x + width / 2, y + height - 40);

            // Draw barcode at bottom (centered)
            this.drawSimpleBarcode(ctx, parcel.parcelBarcode, barcodeX, y + height - 35, barcodeWidth, 20);
        }

        // Reset styles
        ctx.fillStyle = '#000000';
        ctx.textAlign = 'left';
    }  /**
   * Draw a Code-128 barcode using JsBarcode library on canvas
   * @param ctx Canvas 2D context
   * @param code Barcode string
   * @param x X position
   * @param y Y position
   * @param width Barcode width
   * @param height Barcode height
   */
    private drawSimpleBarcode(
        ctx: CanvasRenderingContext2D,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        try {
            // Create a temporary canvas for barcode generation
            const barcodeCanvas = document.createElement('canvas');

            // Generate Code-128 barcode using JsBarcode
            JsBarcode(barcodeCanvas, code, {
                format: "CODE128",
                width: 2,
                height: 100,
                displayValue: false, // We'll add text separately
                margin: 0,
                background: "#ffffff",
                lineColor: "#000000"
            });

            // Draw the generated barcode on the main canvas
            ctx.drawImage(barcodeCanvas, x, y, width, height);
            // Add the code text below the barcode
            ctx.font = 'bold 8px monospace';
            ctx.textAlign = 'center';
            ctx.fillStyle = '#000000';
            ctx.fillText(code, x + width / 2, y + height + 10); // Increased from +6 to +10
            ctx.textAlign = 'left';

        } catch (error) {
            console.error('Error generating barcode with JsBarcode on canvas:', error);
            // Fallback to simple rectangle
            this.drawFallbackBarcodeOnCanvas(ctx, code, x, y, width, height);
        }
    }

    /**
     * Fallback barcode drawing method for canvas
     * @param ctx Canvas 2D context
     * @param code Barcode string
     * @param x X position
     * @param y Y position
     * @param width Barcode width
     * @param height Barcode height
     */
    private drawFallbackBarcodeOnCanvas(
        ctx: CanvasRenderingContext2D,
        code: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        // Simple fallback - draw a rectangle with text
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 1;
        ctx.strokeRect(x, y, width, height);
        ctx.font = 'bold 8px Arial';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#000000';
        ctx.fillText('BARCODE', x + width / 2, y + height / 2);
        ctx.fillText(code, x + width / 2, y + height + 10); // Increased from +6 to +10
        ctx.textAlign = 'left';
    }

    /**
     * Download a blob as a file
     * @param blob The blob to download
     * @param filename The filename for the download
     */
    private downloadBlob(blob: Blob, filename: string): void {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Clean up the URL
        URL.revokeObjectURL(url);
    }
    /**
     * Generate multiple format options for labels
     * @param parcels Array of parcels
     * @param waybillCode Waybill code
     * @param format Output format ('png' | 'pdf')
     * @param dealerInfo Dealer information
     * @returns Observable with success status
     */
    generateLabelsWithFormat(
        parcels: Parcel[],
        waybill: Waybill,
        format: 'png' | 'pdf' = 'pdf',
        dealerInfo?: any
    ): Observable<boolean> {
        if (format === 'pdf') {
            return this.generatePDFLabels(parcels, waybill, dealerInfo);
        } else {
            // Keep old canvas-based PNG generation as backup
            return this.generateWaybillLabelsAsPNG(parcels, waybill.waybillCode, dealerInfo);
        }
    }
    /**
     * Generate PDF labels using canvas and convert to PDF
     * @param parcels Array of parcels
     * @param waybillCode Waybill code
     * @param dealerInfo Dealer information
     * @returns Observable with success status
     */
    private generatePDFLabels(parcels: Parcel[], waybill: Waybill, dealerInfo?: any): Observable<boolean> {
        // Now we use the native PDF generation instead of fallback
        return this.generateWaybillLabels(parcels, waybill, dealerInfo);
    }

    /**
     * Generate labels as PNG (backup method using canvas)
     * @param parcels Array of parcels for the waybill
     * @param waybillCode The waybill code
     * @param dealerInfo Dealer information for the label
     * @returns Observable with success status
     */
    generateWaybillLabelsAsPNG(parcels: Parcel[], waybillCode: string, dealerInfo?: any): Observable<boolean> {
        try {
            // Create a canvas for label generation
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            if (!ctx) {
                throw new Error('Canvas context not available');
            }

            // Set canvas dimensions for multiple labels (A4 format consideration)
            const labelWidth = 400;
            const labelHeight = 200;
            const labelsPerRow = 2;
            const labelsPerColumn = Math.ceil(parcels.length / labelsPerRow);

            canvas.width = labelWidth * labelsPerRow;
            canvas.height = labelHeight * labelsPerColumn;

            // Set white background
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);

            // Generate label for each parcel
            parcels.forEach((parcel, index) => {
                const row = Math.floor(index / labelsPerRow);
                const col = index % labelsPerRow;
                const x = col * labelWidth;
                const y = row * labelHeight;

                this.drawParcelLabel(ctx, parcel, x, y, labelWidth, labelHeight, waybillCode, dealerInfo);
            });

            // Convert canvas to blob and download
            canvas.toBlob((blob) => {
                if (blob) {
                    this.downloadBlob(blob, `Labels_${waybillCode}_${new Date().toISOString().slice(0, 10)}.png`);
                }
            }, 'image/png');

            return of(true);
        } catch (error) {
            console.error('Error generating waybill labels as PNG:', error);
            return of(false);
        }
    }

    /**
     * Validate parcel data before label generation
     * @param parcel Parcel to validate
     * @returns True if parcel has minimum required data
     */
    private validateParcelData(parcel: Parcel): boolean {
        return !!(parcel.parcelCode || parcel.parcelBarcode);
    }

    /**
     * Get label generation summary
     * @param parcels Array of parcels
     * @returns Summary object with counts and validation info
     */
    getLabelGenerationSummary(parcels: Parcel[]): {
        totalParcels: number;
        validParcels: number;
        invalidParcels: number;
        hasBarcodeParcels: number;
        estimatedLabels: number;
    } {
        const validParcels = parcels.filter(p => this.validateParcelData(p));
        const hasBarcodeParcels = parcels.filter(p => p.parcelBarcode);

        return {
            totalParcels: parcels.length,
            validParcels: validParcels.length,
            invalidParcels: parcels.length - validParcels.length,
            hasBarcodeParcels: hasBarcodeParcels.length,
            estimatedLabels: validParcels.length
        };
    }    
    
    /**
     * Load and add logo to PDF label
     * @param pdf jsPDF instance
     * @param x X position in mm
     * @param y Y position in mm
     * @param width Logo width in mm
     * @param height Logo height in mm
     */
    private addLogoToPDF(
        pdf: jsPDF,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        try {
            // Create a professional logo placeholder with company name
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.5);
            pdf.rect(x, y, width, height);
            
            // Fill with light gray background
            pdf.setFillColor(240, 240, 240);
            pdf.rect(x, y, width, height, 'F');
            
            // Add border
            pdf.setDrawColor(0, 0, 0);
            pdf.setLineWidth(0.3);
            pdf.rect(x, y, width, height, 'S');

            // Add company name as professional text logo
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text('BERTANI', x + width/2, y + height/2 - 1, { align: 'center' });
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(6);
            pdf.text('TRASPORTI', x + width/2, y + height/2 + 2, { align: 'center' });

            // Reset color
            pdf.setTextColor(0, 0, 0);
        } catch (error) {
            console.warn('Could not create logo placeholder:', error);
            // Simple fallback: just text
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(0, 0, 0);
            pdf.text('BERTANI', x + width/2, y + height/2, { align: 'center' });
        }
    }

    /**
     * Load SVG logo and convert to image for PDF
     * @param pdf jsPDF instance
     * @param x X position in mm
     * @param y Y position in mm
     * @param width Logo width in mm
     * @param height Logo height in mm
     */
    private async loadSVGLogo(
        pdf: jsPDF,
        x: number,
        y: number,
        width: number,
        height: number
    ): Promise<void> {
        try {
            const response = await fetch('/img/logo-bertani-color.svg');
            const svgText = await response.text();
            
            // Create SVG element
            const svg = new DOMParser().parseFromString(svgText, 'image/svg+xml').documentElement;
            
            // Create canvas to convert SVG to PNG
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            const img = new Image();
            
            // Convert SVG to data URL
            const svgData = new XMLSerializer().serializeToString(svg);
            const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(svgBlob);
            
            return new Promise((resolve, reject) => {
                img.onload = () => {
                    canvas.width = img.width || 200;
                    canvas.height = img.height || 80;
                    
                    if (ctx) {
                        // Fill white background
                        ctx.fillStyle = 'white';
                        ctx.fillRect(0, 0, canvas.width, canvas.height);
                        
                        // Draw SVG
                        ctx.drawImage(img, 0, 0);
                        
                        // Convert to PNG and add to PDF
                        const pngData = canvas.toDataURL('image/png');
                        pdf.addImage(pngData, 'PNG', x, y, width, height);
                    }
                    
                    URL.revokeObjectURL(url);
                    resolve();
                };
                
                img.onerror = () => {
                    URL.revokeObjectURL(url);
                    // Fallback to placeholder
                    this.addLogoToPDF(pdf, x, y, width, height);
                    resolve();
                };
                
                img.src = url;
            });
            
        } catch (error) {
            console.warn('Could not load SVG logo:', error);
            // Fallback to placeholder
            this.addLogoToPDF(pdf, x, y, width, height);
        }
    }

    /**
     * Load PNG logo image and add to PDF
     * @param pdf jsPDF instance
     * @param logoUrl URL of the logo image
     * @param x X position in mm
     * @param y Y position in mm
     * @param width Logo width in mm
     * @param height Logo height in mm
     */
    private loadImageLogo(
        pdf: jsPDF,
        logoUrl: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): void {
        try {
            // Create image element
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            
            img.onload = () => {
                try {
                    // Convert image to base64 using a temporary canvas
                    const tempCanvas = document.createElement('canvas');
                    tempCanvas.width = img.width;
                    tempCanvas.height = img.height;
                    const tempCtx = tempCanvas.getContext('2d');
                    
                    if (tempCtx) {
                        // Fill white background
                        tempCtx.fillStyle = 'white';
                        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
                        
                        // Draw image
                        tempCtx.drawImage(img, 0, 0);
                        
                        // Convert to data URL and add to PDF
                        const imgData = tempCanvas.toDataURL('image/png');
                        pdf.addImage(imgData, 'PNG', x, y, width, height);
                        
                        console.log('Logo caricato con successo');
                    } else {
                        throw new Error('Cannot get canvas context');
                    }
                } catch (e) {
                    console.warn('Errore durante la conversione dell\'immagine:', e);
                    // Fallback to placeholder if image conversion fails
                    this.addLogoToPDF(pdf, x, y, width, height);
                }
            };
            
            img.onerror = () => {
                console.warn('Errore durante il caricamento dell\'immagine:', logoUrl);
                // Fallback to placeholder if image loading fails
                this.addLogoToPDF(pdf, x, y, width, height);
            };
              // Check if image is already loaded (from cache)
            if (img.complete && img.naturalWidth !== 0) {
                // Trigger onload manually for cached images
                const loadEvent = new Event('load');
                img.dispatchEvent(loadEvent);
            } else {
                // Load the image
                img.src = logoUrl;
                
                // Add timeout to prevent hanging
                setTimeout(() => {
                    if (!img.complete) {
                        console.warn('Timeout durante il caricamento dell\'immagine, uso placeholder');
                        this.addLogoToPDF(pdf, x, y, width, height);
                    }
                }, 3000); // 3 second timeout
            }
            
        } catch (error) {
            console.warn('Errore generale durante il caricamento del logo:', error);
            // Fallback to placeholder
            this.addLogoToPDF(pdf, x, y, width, height);
        }
    }

    /**
     * Alternative method: Load logo synchronously using fetch
     * @param pdf jsPDF instance
     * @param logoUrl URL of the logo image
     * @param x X position in mm
     * @param y Y position in mm
     * @param width Logo width in mm
     * @param height Logo height in mm
     */
    private async loadImageLogoSync(
        pdf: jsPDF,
        logoUrl: string,
        x: number,
        y: number,
        width: number,
        height: number
    ): Promise<void> {
        try {
            console.log('Tentativo di caricamento sincrono del logo:', logoUrl);
            
            const response = await fetch(logoUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            
            const blob = await response.blob();
            const reader = new FileReader();
            
            return new Promise((resolve, reject) => {
                reader.onload = () => {
                    try {
                        const base64data = reader.result as string;
                        pdf.addImage(base64data, 'PNG', x, y, width, height);
                        console.log('Logo caricato con successo (sincrono)');
                        resolve();
                    } catch (e) {
                        console.warn('Errore durante l\'aggiunta dell\'immagine al PDF:', e);
                        this.addLogoToPDF(pdf, x, y, width, height);
                        resolve();
                    }
                };
                
                reader.onerror = () => {
                    console.warn('Errore durante la lettura del blob dell\'immagine');
                    this.addLogoToPDF(pdf, x, y, width, height);
                    resolve();
                };
                
                reader.readAsDataURL(blob);
            });
            
        } catch (error) {
            console.warn('Errore durante il caricamento sincrono del logo:', error);
            // Fallback to placeholder
            this.addLogoToPDF(pdf, x, y, width, height);
        }
    }    /**
     * Generate QR code for parcel code
     * @param parcelCode The parcel code to encode
     * @param size QR code size in pixels
     * @returns Promise with QR code data URL
     */
    private async generateQRCode(parcelCode: string, size: number = 128): Promise<string> {
        try {
            const qrCodeDataUrl = await QRCode.toDataURL(parcelCode, {
                width: size,
                margin: 1,
                color: {
                    dark: '#000000',
                    light: '#FFFFFF'
                },
                errorCorrectionLevel: 'M'
            });
            return qrCodeDataUrl;
        } catch (error) {
            console.error('Error generating QR code:', error);
            // Return fallback placeholder
            return this.generateFallbackQRCode(size);
        }
    }

    /**
     * Generate fallback QR code placeholder
     * @param size QR code size in pixels
     * @returns Fallback data URL
     */
    private generateFallbackQRCode(size: number): string {
        const canvas = document.createElement('canvas');
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext('2d');
        
        if (ctx) {
            // Draw white background
            ctx.fillStyle = '#FFFFFF';
            ctx.fillRect(0, 0, size, size);
            
            // Draw border
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 2;
            ctx.strokeRect(2, 2, size - 4, size - 4);
            
            // Draw "QR" text
            ctx.fillStyle = '#000000';
            ctx.font = 'bold 16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('QR', size / 2, size / 2 + 6);
        }
        
        return canvas.toDataURL('image/png');
    }
}
